import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffSharpIcon from "@mui/icons-material/FilterAltOffSharp";
import SearchIcon from "@mui/icons-material/Search";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import inputStyles from "../../../../assets/styles/components/input.module.css";
import {
  cancelFinancialEntry,
  fetchPaymentTypeByTitle,
  loadExpensesEntries,
  markFinancialEntryAsPaid,
  refoundFinancialEntry,
} from "../../../../services/financial";
import { getFinanceAccounts } from "../../../../services/cashFlow";
import type { IordemExpense } from "../../../../types/financial";
import type { movimentType } from "../../../../types/cashFlow";
import type {
  IFinancialEntry,
  IFinancialFilter,
  ILoadFinancialEntriesResponse,
} from "../../../../types/financial";
import Button from "../../../../components/Button/Button";
import Modal from "../CashFlow/Modal";
import ExpenseFilterModal from "./ExpenseFilterModal/ExpenseFilterModal";
import styles from "./Expenses.module.css";
import ExpensesEntriesList from "./ExpensesEntriesList";
import ModalDelete from "../../../../components/ModalDelete/ModalDelete";
import { useIsMobile } from "../../../../utils/useIsMobile";
import InputSearch from "../../../../components/InputSearch/InputSearch";

// ─── Chaves do sessionStorage ────────────────────────────────────────────────
const SESSION_PAGES_KEY = "expenses_pages";
const SESSION_SCROLL_KEY = "expenses_scroll";

const getTodayFormatted = () => {
  const today = new Date();
  const year = today.getUTCFullYear();
  const month = String(today.getUTCMonth() + 1).padStart(2, "0");
  const day = String(today.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

type FinancialStatusFilter = "aberto" | "pago" | "atrasado" | "cancelado";
type CardFilterType = FinancialStatusFilter | "vencemHoje";

const statusMapFinancial = (
  status?: "aberto" | "pago" | "atrasado" | "cancelado" | "pagar" | "receber",
) => {
  switch (status) {
    case "aberto":
      return { text: "Em aberto", color: "blue-finance" };
    case "pago":
      return { text: "Pago", color: "green-finance" };
    case "atrasado":
      return { text: "Atrasado", color: "red-finance" };
    case "cancelado":
      return { text: "Cancelado", color: "gray-finance" };
    case "receber":
      return { text: "A Receber", color: "green-type" };
    case "pagar":
      return { text: "A Pagar", color: "yellow-type" };
    default:
      return { text: "N/A", color: "default" };
  }
};

// ─── Helpers de (de)serialização de URL params ───────────────────────────────
type OrderField = keyof IordemExpense;

function buildOrderFromParams(params: URLSearchParams): IordemExpense {
  const field = params.get("orderField") as OrderField | null;
  const dir = params.get("orderDir") as "ASC" | "DESC" | null;
  if (field && dir) {
    return { [field]: dir } as IordemExpense;
  }
  return { codigo_estabelecimento: "DESC" };
}

function buildFilterFromParams(params: URLSearchParams): IFinancialFilter {
  return {
    tipo: (params.get("tipo") as any) || "pagar",
    uid_fornecedor: params.get("uid_fornecedor") || undefined,
    status: (params.get("status") as any) || undefined,
    uid_tipo_pagamento: params.get("uid_tipo_pagamento") || undefined,
    date_initial: params.get("date_initial") || undefined,
    date_final: params.get("date_final") || undefined,
    uid_conta_financeira: params.get("uid_conta_financeira") || undefined,
    uid_plano_contas: params.get("uid_plano_contas") || undefined,
    uid_centro_custo: params.get("uid_centro_custo") || undefined,
    // @ts-ignore Permitindo prop period pro modal
    period: params.get("period") || undefined,
  };
}

const Expenses = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();

  // ── Estado derivado da URL ──────────────────────────────────────────────────
  const search = searchParams.get("search") ?? "";
  const filterValues = useMemo(() => buildFilterFromParams(searchParams), [searchParams]);
  const orderTable = useMemo(() => buildOrderFromParams(searchParams), [searchParams]);

  // ── Estado local de UI ─────────────────────────────────────────────────────
  const [isHidden, setIsHidden] = useState({ filter: false, movement: true, payment: true });
  const [tempSearch, setTempSearch] = useState(search);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [entryToRefound, setEntryToRefound] = useState<IFinancialEntry | undefined>(undefined);
  const [entryToPay, setEntryToPay] = useState<IFinancialEntry | undefined>(undefined);
  const [tempPaymentData, setTempPaymentData] = useState<{ uidType?: string; titleType?: string; } | null>(null);
  const [entryToCancel, setEntryToCancel] = useState<IFinancialEntry | null>(null);
  const [showNoBankModal, setShowNoBankModal] = useState(false);

  // Carrega contas financeiras para verificação prévia ao dar baixa
  const { data: financeAccounts } = useQuery({
    queryKey: ["getFinanceAccounts"],
    queryFn: getFinanceAccounts,
  });

  // ── Ref para o container com scroll ────────────────────────────────────────
  // Atenção: Você precisará repassar isso para dentro do seu componente ExpensesEntriesList
  // para que seja atrelado à prop tbodyRef do TableContainer
  const tbodyRef = useRef<any>(null);

  // ── Controle de restauração ─────────────────────────────────────────────────
  const [targetPages, setTargetPages] = useState<number>(() => {
    const saved = sessionStorage.getItem(SESSION_PAGES_KEY);
    return saved ? parseInt(saved, 10) : 1;
  });
  const [scrollRestored, setScrollRestored] = useState(false);
  const isRestoringRef = useRef(targetPages > 1);

  // ─── Helpers para atualizar URL ────────────────────────────────────────────
  const updateParams = (updates: Record<string, string | boolean | null | undefined>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    }, { replace: true });
  };

  // ─── Infinite query ────────────────────────────────────────────────────────
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, refetch } =
    useInfiniteQuery<ILoadFinancialEntriesResponse>({
      queryKey: ["financialEntries", search, filterValues, orderTable],
      queryFn: ({ pageParam }) =>
        loadExpensesEntries(search, pageParam as number, filterValues, orderTable),
      getNextPageParam: (lastPage, allPages) => {
        const totalCarregado = allPages.reduce((acc, page) => acc + page.rows.length, 0);
        return totalCarregado < lastPage.count ? allPages.length + 1 : undefined;
      },
      initialPageParam: 1,
      refetchOnWindowFocus: true,
    });

  const isLoading = isFetching && !isFetchingNextPage;

  const dataRender: IFinancialEntry[] = useMemo(() => {
    if (!data) return [];
    return data.pages.flatMap((page) => page.rows);
  }, [data]);

  const financialStats = useMemo(() => {
    return (
      data?.pages?.[0]?.stats ?? { vencidos: 0, vencemHoje: 0, aVencer: 0, pagos: 0, total: 0 }
    );
  }, [data]);

  // ─── Restauração de páginas ────────────────────────────────────────────────
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    sessionStorage.removeItem(SESSION_PAGES_KEY);
    sessionStorage.removeItem(SESSION_SCROLL_KEY);
    setTargetPages(1);
    setScrollRestored(false);
    isRestoringRef.current = false;
  }, [search, filterValues, orderTable]);

  useEffect(() => {
    if (!isRestoringRef.current) return;
    if (!data) return;

    const currentPages = data.pages.length;
    if (currentPages < targetPages && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    } else if (currentPages >= targetPages) {
      isRestoringRef.current = false;
    }
  }, [data, targetPages, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Salvar quantidade de páginas no sessionStorage
  useEffect(() => {
    if (isRestoringRef.current) return;
    const pages = data?.pages.length ?? 1;
    sessionStorage.setItem(SESSION_PAGES_KEY, String(pages));
  }, [data?.pages.length]);

  // ─── Restauração do scroll após páginas carregadas ─────────────────────────
  useEffect(() => {
    if (scrollRestored) return;
    if (isRestoringRef.current) return;

    const el = tbodyRef.current;
    if (!el) return;

    const savedScroll = sessionStorage.getItem(SESSION_SCROLL_KEY);
    if (!savedScroll) {
      setScrollRestored(true);
      return;
    }

    const scrollY = parseInt(savedScroll, 10);

    requestAnimationFrame(() => {
      el.scrollTop = scrollY;
      setScrollRestored(true);
    });
  }, [data?.pages.length, scrollRestored]);

  // ─── Salvar posição do scroll (debounce 200ms) ─────────────────────────────
  useEffect(() => {
    const el = tbodyRef.current;
    if (!el) return;

    let debounceTimer: ReturnType<typeof setTimeout>;

    const handleScroll = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        sessionStorage.setItem(SESSION_SCROLL_KEY, String(el.scrollTop));
      }, 200);
    };

    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", handleScroll);
      clearTimeout(debounceTimer);
    };
  }, []);

  // ─── Debounce da busca ─────────────────────────────────────────────────────
  useEffect(() => {
    const handler = setTimeout(() => {
      updateParams({ search: tempSearch || undefined });
    }, 500);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempSearch]);

  const { mutate: cancelMutation, isPending: isCancelling } = useMutation({
    mutationFn: (uid: string) => cancelFinancialEntry(uid),
    onSuccess: async () => {
      toast.success("Lançamento financeiro cancelado com sucesso!");
      setEntryToCancel(null);
      await refetch();
    },
    onError: (err: any) => {
      toast.error(err.message || "Erro ao cancelar lançamento.");
      setEntryToCancel(null);
    },
  });
  const { mutate: mutate_refound } = useMutation({
    mutationFn: async (uid: string) => await refoundFinancialEntry(uid),
    onSuccess: async (response) => {
      toast.success(response.message);
      setEntryToRefound(undefined);
      await refetch();
    },
    onError: async (err) => {
      toast.error(err.message);
      setEntryToRefound(undefined);
      await refetch();
    },
  });

  useEffect(() => {
    setIsFilterApplied(
      filterValues.uid_fornecedor || filterValues.status || filterValues.uid_tipo_pagamento || filterValues.date_initial || filterValues.date_final ? true : false
    );
  }, [filterValues]);

  // ─── Handlers de Filtros e URL ─────────────────────────────────────────────
  const handleResetFilter = () => {
    setTempSearch("");
    setSearchParams(new URLSearchParams({ tipo: "pagar" }), { replace: true });
  };

  const handleCardClick = (filterType: CardFilterType) => {
    const todayFormatted = getTodayFormatted();
    const statusOnlyFilters: FinancialStatusFilter[] = ["atrasado", "aberto", "pago"];

    const isVencemHojeFilterActive =
      filterType === "vencemHoje" &&
      filterValues.date_initial === todayFormatted &&
      filterValues.date_final === todayFormatted &&
      filterValues.status === "aberto";

    const isStatusOnlyFilterActive =
      statusOnlyFilters.includes(filterType as FinancialStatusFilter) &&
      filterValues.status === filterType &&
      !filterValues.date_initial &&
      !filterValues.date_final;

    let nextFilters: Record<string, any> = { tipo: "pagar" };

    if (isStatusOnlyFilterActive || isVencemHojeFilterActive) {
      // Desativa o filtro se clicar novamente no card ativo
      nextFilters = { ...filterValues, status: undefined, date_initial: undefined, date_final: undefined, tipo: "pagar" };
    } else if (filterType === "vencemHoje") {
      nextFilters = { ...filterValues, status: "aberto", date_initial: todayFormatted, date_final: todayFormatted, tipo: "pagar" };
    } else if (statusOnlyFilters.includes(filterType as FinancialStatusFilter)) {
      nextFilters = { ...filterValues, status: filterType, date_initial: undefined, date_final: undefined, tipo: "pagar" };
    }

    // @ts-ignore Limpeza de propriedades
    updateParams(nextFilters);
  };

  const handleSetFilterModal = (newFilter: IFinancialFilter) => {
    updateParams({ ...newFilter, tipo: "pagar" });
  };

  const handleSetOrderTable = (updater: any) => {
    const nextOrder = typeof updater === "function" ? updater(orderTable) : updater;
    const field = Object.keys(nextOrder)[0];
    const dir = nextOrder[field];
    updateParams({ orderField: field, orderDir: dir });
  };

  const handleCancelEntry = async (entry: IFinancialEntry) => {
    setEntryToCancel(entry);
  };

  useEffect(() => {
    if (!entryToRefound) return;
    mutate_refound(entryToRefound.uid_financeiro_lancamento);
  }, [entryToRefound]);

  const handleOpenPaymentModal = (entry: IFinancialEntry) => {
    // Verifica se há contas bancárias cadastradas antes de abrir o modal
    if (!financeAccounts || financeAccounts.length === 0) {
      setShowNoBankModal(true);
      return;
    }
    setEntryToPay(entry);
    const paymentTypeTitle = entry.tipo_pagamento?.titulo;
    if (paymentTypeTitle) {
      handleProceedToMovement(paymentTypeTitle, undefined, entry);
    } else {
      handleProceedToMovement("Sem definição", undefined, entry);
    }
  };

  const handleProceedToMovement = async (paymentTitle: string, paymentUid?: string, entryOverride?: IFinancialEntry) => {
    const targetEntry = entryOverride || entryToPay;
    if (!targetEntry) return;

    let finalPaymentUid = paymentUid;
    let finalPaymentTitle = paymentTitle;

    if (!paymentUid && paymentTitle !== "Sem definição") {
      try {
        const paymentType = await fetchPaymentTypeByTitle(paymentTitle);
        if (paymentType) {
          finalPaymentUid = paymentType.uid_tipo_pagamento;
          finalPaymentTitle = paymentType.titulo;
        }
      } catch (error) {
        console.error("Erro ao buscar tipo de pagamento", error);
      }
    }

    setTempPaymentData({ uidType: finalPaymentUid, titleType: finalPaymentTitle });
    setIsHidden((prev) => ({ ...prev, payment: true, movement: false }));
  };

  const getMovementInitialData = (): Partial<movimentType> | undefined => {
    if (!entryToPay) return undefined;
    return {
      descricao: `Pagamento Despesa: ${entryToPay.descricao} - Fornecedor: ${entryToPay.fornecedor?.nome_fantasia || "N/A"}`,
      valor: entryToPay.valor_parcela,
      tipo: "saida",
      data_movimentacao: new Date().toISOString().split("T")[0],
      uid_conta_financeira: entryToPay.uid_conta_financeira || "",
      uid_financeiro_lancamento: entryToPay.uid_financeiro_lancamento,
      uid_plano_contas: entryToPay.uid_plano_contas,
      uid_centro_custo: entryToPay.uid_centro_custo,
    };
  };

  const handleFinalizePayment = async (data_movimentacao?: string | null) => {
    if (!entryToPay) return;
    setIsHidden((prev) => ({ ...prev, movement: true }));

    try {
      const entryId = entryToPay.uid_financeiro_lancamento;
      const paymentDate = data_movimentacao ?? new Date().toISOString();

      await markFinancialEntryAsPaid(entryId, paymentDate, tempPaymentData?.uidType);
      toast.success("Despesa baixada e movimentação de saída gerada com sucesso!");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro desconhecido ao quitar lançamento.");
    } finally {
      setEntryToPay(undefined);
      await refetch();
    }
  };

  const handleCopyEntry = (entry: IFinancialEntry): void => {
    const entryCopy = {
      ...entry,
      uid_financeiro_lancamento: "",
      data_emissao: getTodayFormatted(),
      data_competencia: null,
      data_vencimento: "",
      data_pagamento: null,
      status: "aberto",
    };
    navigate("add", { state: { entryToCopy: entryCopy } });
  };

  return (
    <div className={styles.containerProjects}>
      <div className={styles.purchases}>
        <div className={styles.header}>
          <h1>Contas a Pagar</h1>

          <InputSearch
            searchValue={tempSearch}
            setSearchValue={setTempSearch}
            placeholder="Pesquisar"
          />

          <div className={styles.headerButtons}>
            <p>Qtd:<span>{data?.pages?.[0]?.count ?? 0}</span></p>
            <Button variant="contain" onClick={handleResetFilter} title="Limpar filtros">
              <FilterAltOffSharpIcon />
            </Button>
            <Button variant="contain" title="Filtrar lançamentos" onClick={() => setIsHidden((prev) => ({ ...prev, filter: true }))}>
              <FilterAltIcon />
            </Button>
            <Button variant="contain" onClick={() => navigate("add")} title="Criar despesa">
              <AddIcon />
            </Button>
            <Button variant="contain" title="Voltar para página anterior" onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </Button>
          </div>
        </div>

        <div className={styles.financialStatsCard}>
          <div className={styles.statLine}>
            {(() => {
              const todayFormatted = getTodayFormatted();
              const isVencemHojeActive =
                filterValues.status === "aberto" &&
                filterValues.date_initial === todayFormatted &&
                filterValues.date_final === todayFormatted;
              return (
                <>
                  <div
                    className={`${styles.statItem} ${styles.vencidosCard} ${filterValues.status === "atrasado" ? styles.activeFilter : ""}`}
                    onClick={() => handleCardClick("atrasado")}
                  >
                    <span className={styles.vencidosText}>Vencidos</span>
                    <span className={styles.vencidosCount}>{isLoading ? "..." : financialStats.vencidos}</span>
                  </div>
                  <div
                    className={`${styles.statItem} ${styles.vencemHojeCard} ${isVencemHojeActive ? styles.activeFilter : ""}`}
                    onClick={() => handleCardClick("vencemHoje")}
                  >
                    <span className={styles.vencemHojeText}>Vencem Hoje</span>
                    <span className={styles.vencemHojeCount}>{isLoading ? "..." : financialStats.vencemHoje}</span>
                  </div>
                  <div
                    className={`${styles.statItem} ${styles.aVencerCard} ${filterValues.status === "aberto" && !isVencemHojeActive ? styles.activeFilter : ""}`}
                    onClick={() => handleCardClick("aberto")}
                  >
                    <span className={styles.aVencerText}>Em Aberto</span>
                    <span className={styles.aVencerCount}>{isLoading ? "..." : financialStats.aVencer}</span>
                  </div>
                  <div
                    className={`${styles.statItem} ${styles.pagosCard} ${filterValues.status === "pago" ? styles.activeFilter : ""}`}
                    onClick={() => handleCardClick("pago")}
                  >
                    <span className={styles.pagosText}>Pagos</span>
                    <span className={styles.pagosCount}>{isLoading ? "..." : financialStats.pagos}</span>
                  </div>
                </>
              );
            })()}

            <div className={`${styles.statItem} ${styles.totalCard} ${styles.totalCardVisual}`}>
              <span className={styles.totalText}>Total</span>
              <span className={styles.totalCount} style={financialStats.total && financialStats.total < 1000000 ? { fontSize: "1.9rem" } : { fontSize: "1.5rem" }}>
                {isLoading ? "..." : isFilterApplied ? (financialStats.total ? financialStats.total.toLocaleString("pt-br", { style: "currency", currency: "BRL" }) : "-") : "-"}
              </span>
            </div>
          </div>
        </div>

        <ExpensesEntriesList
          tbodyRef={tbodyRef}
          rows={dataRender}
          handleCancelEntry={handleCancelEntry}
          statusMapper={statusMapFinancial}
          handleRefound={(entry) => setEntryToRefound(entry)}
          handleCopyEntry={handleCopyEntry}
          handlePayEntry={handleOpenPaymentModal}
          orderTable={orderTable}
          setOrderTable={handleSetOrderTable}
        />

        <div className={styles.loadMore}>
          {hasNextPage ? (
            <Button variant="contain" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
              {isFetchingNextPage ? "Carregando..." : "Carregar mais registros"}
            </Button>
          ) : dataRender.length > 0 ? (
            <p className={styles.tableLastItem}>Todos os registros foram carregados</p>
          ) : null}
        </div>
      </div>

      {isHidden.filter && (
        <ExpenseFilterModal
          setValuesInput={handleSetFilterModal}
          valuesInput={filterValues}
          onClose={() => setIsHidden((prev) => ({ ...prev, filter: false }))}
        />
      )}

      {!isHidden.movement && entryToPay && (
        <Modal
          title="Gerar Movimentação (Baixa Despesa)"
          onclose={() => setIsHidden((prev) => ({ ...prev, movement: true }))}
          initialData={getMovementInitialData()}
          onSuccessExternal={handleFinalizePayment}
          lockEntryType={true}
        />
      )}
      
      <ModalDelete
        isOpen={!!entryToCancel}
        onClose={() => setEntryToCancel(null)}
        onConfirm={() => entryToCancel && cancelMutation(entryToCancel.uid_financeiro_lancamento)}
        isDeleting={isCancelling}
        entity="lançamento"
        title={entryToCancel?.descricao || "este item"}
        isMobile={isMobile}
      />

      {/* Modal: Nenhuma conta financeira cadastrada */}
      {showNoBankModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
        }}>
          <div style={{
            background: "#fff", borderRadius: 16, padding: "2rem",
            maxWidth: 420, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            display: "flex", flexDirection: "column", gap: "1rem",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: "2rem" }}>🏦</span>
              <h2 style={{ margin: 0, fontSize: "1.1rem", color: "#333" }}>
                Conta financeira obrigatória
              </h2>
            </div>
            <p style={{ margin: 0, color: "#555", fontSize: "0.95rem", lineHeight: 1.5 }}>
              Para dar baixa em uma despesa é necessário ter ao menos uma
              <strong> conta financeira</strong> cadastrada. Deseja cadastrar agora?
            </p>
            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
              <Button variant="outlined" onClick={() => setShowNoBankModal(false)}>
                Cancelar
              </Button>
              <Button variant="contain" onClick={() => {
                setShowNoBankModal(false);
                navigate("/agro/financialHub/financialAccounts");
              }}>
                Cadastrar Conta
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;